import {useState, useEffect, useRef} from 'react';
import postSearchCustomers from '../functions/API/postSearchCustomers.js';

export default function UserSearchSelect({value, onChange, placeholder = 'Search by name, email, or address...'}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced search
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (query.length < 2) {
            setResults([]);
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        debounceRef.current = setTimeout(async () => {
            const response = await postSearchCustomers(query);
            if (response.response.ok && response.data?.users) {
                setResults(response.data.users);
                setIsOpen(true);
            } else {
                setResults([]);
            }
            setIsLoading(false);
        }, 300);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [query]);

    function handleSelect(user) {
        setSelectedUser(user);
        setQuery('');
        setIsOpen(false);
        setResults([]);
        onChange(user.id);
    }

    function handleClear() {
        setSelectedUser(null);
        setQuery('');
        setResults([]);
        onChange('');
    }

    return (
        <div ref={wrapperRef} className="relative">
            {selectedUser ? (
                <div className="flex items-center justify-between p-2.5 rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 shadow-sm">
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {selectedUser.firstname} {selectedUser.lastname}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{selectedUser.email}</p>
                        {selectedUser.address && (
                            <p className="text-xs text-gray-400 truncate">{selectedUser.address}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleClear}
                        className="ml-2 shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm"
                    >
                        Clear
                    </button>
                </div>
            ) : (
                <div className="relative">
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => { if (results.length > 0) setIsOpen(true); }}
                        placeholder={placeholder}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6 pl-2"
                    />
                    {isLoading && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <div className="h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                        </div>
                    )}
                </div>
            )}

            {isOpen && results.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-zinc-800 rounded-md border border-gray-200 dark:border-zinc-700 shadow-lg max-h-60 overflow-y-auto">
                    {results.map((user) => (
                        <button
                            key={user.id}
                            type="button"
                            onClick={() => handleSelect(user)}
                            className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-zinc-700 border-b border-gray-100 dark:border-zinc-700 last:border-b-0"
                        >
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {user.firstname} {user.lastname}
                                </p>
                                {user.agreementreference && (
                                    <span className="text-xs text-gray-400 ml-2 shrink-0">
                                        Ref: {user.agreementreference}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{user.email}</p>
                            {user.address && (
                                <p className="text-xs text-gray-400 mt-0.5">{user.address}</p>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {isOpen && query.length >= 2 && results.length === 0 && !isLoading && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-zinc-800 rounded-md border border-gray-200 dark:border-zinc-700 shadow-lg">
                    <p className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">No users found.</p>
                </div>
            )}
        </div>
    );
}
